// Moved from `src/hooks/mutation/useMutateActivateTrial.tsx` (now a delegate)
// at the close-out, ARCFE-979. Deferred at D2 batch 3 "to D3 with billing"
// because it performs a billing-cache WRITE — D3 put that surface in-package,
// dissolving the blocker.
//
// The factory was ALREADY port-shaped (`activate` and `afterSuccess` are
// injected), which is why it moves whole. What could not cross:
//   - the error toast + intl (a user-visible effect is an app reaction) —
//     `onActivateError`, fired where the toast fired; the delegate keeps the
//     `AxiosError` shape-check the description logic depends on.
//   - `invalidateCurrentUser` — workspace's app-side invalidator. It crosses
//     as `refreshCurrentUser` so the package can keep the delayed refresh's
//     CONCURRENCY: both legs still run under one `Promise.all`.
//   - the affiliate read inside the billing-cache write — `applyBillingSnapshot`
//     is supplied by the delegate calling the app-side
//     `setUserBillingInfoQueryCacheFromBillingInfo`, which reads the affiliate
//     code and forwards to THIS package's cache command (the D3 layering).

import { logger, sanitizeError } from '@heidi/runtime/logger';
import { useMutation } from '@tanstack/react-query';
import { type ActivateClinicianTrialResponse } from '../../contracts';
import { invalidateCreditAllowance } from '../query/useQueryCreditAllowance';
import { invalidateGamifyTrialExtension } from '../query/useQueryGamifyTrialExtension';
import { invalidateCurrentUserBillingInfo } from '../query/useQueryUserBillingInfo';

export type ActivateTrialResponse = { data?: ActivateClinicianTrialResponse };

export type ActivateTrialMutationConfig = {
  mutationKey: string;
  activate: () => Promise<ActivateTrialResponse>;
  refreshLogContext: string;
  afterSuccess?: (data: ActivateClinicianTrialResponse) => void | Promise<void>;
  /**
   * Applies the returned billing snapshot to the cache. The delegate supplies
   * the app wrapper that reads the affiliate code and calls this package's
   * `setUserBillingInfoQueryCacheFromBillingInfo`.
   */
  applyBillingSnapshot?: (
    billingInfo: NonNullable<ActivateClinicianTrialResponse['billing_info']>
  ) => void;
  /** The workspace-owned half of the delayed refresh. */
  refreshCurrentUser?: () => Promise<unknown> | void;
  /** Fires where the destructive toast fired. */
  onActivateError?: (error: unknown) => void;
};

/**
 * The shared trial-activation mutation.
 *
 * The 3-second delay before the refresh pair is LOAD-BEARING: the backend
 * makes synchronous Stripe calls and the user/billing reads race the webhook
 * without it. Both refresh legs still run CONCURRENTLY under one
 * `Promise.all`, and a refresh failure only logs — activation already
 * succeeded, so surfacing it as a mutation error would be a lie.
 */
export function useMutateActivateTrial({
  mutationKey,
  activate,
  refreshLogContext,
  afterSuccess,
  applyBillingSnapshot,
  refreshCurrentUser,
  onActivateError,
}: ActivateTrialMutationConfig) {
  return useMutation({
    mutationKey: [mutationKey],
    mutationFn: async () => {
      const { data } = await activate();
      if (!data?.success) {
        throw new Error(data?.message || 'Trial activation failed');
      }

      await afterSuccess?.(data);

      if (data.billing_info != null) {
        applyBillingSnapshot?.(data.billing_info);
      }

      void new Promise<void>((resolve) => setTimeout(resolve, 3_000))
        .then(() =>
          Promise.all([
            refreshCurrentUser?.(),
            invalidateCurrentUserBillingInfo(),
            // The Heidi 2 trial provisions an allowance; the popover may have
            // cached a null `/credit-allowance` before it landed.
            invalidateCreditAllowance(),
            // The optimistic snapshot opens the gift gate before the webhook
            // lands, and the backend's eligibility check reads the
            // webhook-written tier — so the first read can cache `eligible: false`.
            invalidateGamifyTrialExtension(),
          ])
        )
        .catch((error: unknown) => {
          logger.error(
            `Failed to refresh user and billing after ${refreshLogContext}`,
            sanitizeError(error)
          );
        });
    },
    onError: (error) => {
      onActivateError?.(error);
    },
  });
}
